import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import {
  BehaviorSubject,
  Observable,
  catchError,
  distinctUntilChanged,
  finalize,
  map,
  of,
  tap,
} from 'rxjs';
import { ErrorHandlerService } from '../../shared/error/error-handler.service';
import { StorageService } from '../../shared/storage/storage.service';
import { AppRoutes } from '../../shared/routes/routes';
import { AUTH_CONSTS, AUTH_URLS } from './auth.const';
import { InactivityModalComponent } from './inactivity-modal/inactivity-modal.component';
import { environment } from 'src/environments/environment';
import { StringOrNullOrUndefined } from 'src/app/shared/utils';
import {
  Auth,
  AuthUser,
  RefreshResponse,
  LoginResponse,
  TwoFactorVerificationRequest,
  TwoFactorResponse,
  TwoFactorResendResponse,
  TwoFactorBackupCodeRequest,
  TwoFactorState,
  TwoFactorStatusResponse,
  TwoFactorMethod,
  TwoFactorSendCodeRequest,
  TwoFactorSendCodeResponse,
  LocationSelectionRequest,
  LocationSelectionResponse,
  ChangeLocationRequest,
  ChangeLocationResponse,
  UserLocationsResponse,
  LocationForSelection,
  LoginResult,
  SharedUserSnapshot,
  SyncSessionPayload,
  SyncSessionSource,
  RefreshLockState,
  AuthSyncMessage,
  AuthErrorCode,
} from './auth.interface';
import { FeatureFlagsV2Service } from '../services/feature-flags.v2.service';
import { IMPERSONATION_CONSTS } from './impersonation.const';
import { LocationContextService } from '../services/location-context.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly storage = inject(StorageService);
  private readonly errorHandler = inject(ErrorHandlerService);
  private readonly router = inject(Router);
  private readonly featureFlags = inject(FeatureFlagsV2Service);
  private readonly locationContext = inject(LocationContextService);
  private readonly dialog = inject(MatDialog);

  private static readonly ADMIN_VIEW_LOCATION_ID = -1;
  private readonly SYNC_CHANNEL = 'aba_auth_sync';
  private readonly REFRESH_LOCK_KEY = 'aba_auth_refresh_lock';
  private readonly REFRESH_LOCK_TTL_MS = 10000;
  private readonly REFRESH_WAIT_TIMEOUT_MS = 12000;
  private readonly ACTIVITY_SYNC_THROTTLE_MS = 5000;
  private readonly INACTIVITY_WARNING_MS = 65 * 1000;
  private syncChannel: BroadcastChannel | null = null;
  private idleTimer: ReturnType<typeof setTimeout> | null = null;
  private inactivityDialogRef: MatDialogRef<
    InactivityModalComponent,
    'refresh' | 'logout'
  > | null = null;
  private suppressInactivityDialogCloseHandling = false;
  private pendingRefreshResolvers: Array<(success: boolean) => void> = [];
  private readonly IDLE_TIMEOUT_FALLBACK_MS =
    (environment.authIdleTimeoutSeconds ?? 1800) * 1000;
  private currentIdleTimeoutMs = this.IDLE_TIMEOUT_FALLBACK_MS;
  private lastActivityAt = Date.now();
  private lastActivityBroadcastAt = 0;
  private twoFactorStateSubject = new BehaviorSubject<TwoFactorState>({
    required: false,
    expiresAt: null,
    attemptsRemaining: 5,
    isVerifying: false,
    error: null,
    developmentMode: false,
    smsServiceActive: false,
    userRole: null,
  });
  public twoFactorState$ = this.twoFactorStateSubject.asObservable();

  private _user: AuthUser | null = null;
  token: string | null = null;
  private isInitializing = false;
  private hasInitialized = false;
  private instanceId = Math.random().toString(36).substring(2, 11);

  // Track authentication state
  private authStateSubject = new BehaviorSubject<
    'loading' | 'authenticated' | 'unauthenticated'
  >('loading');
  public authState$ = this.authStateSubject.asObservable();

  get user(): AuthUser | null {
    return this._user;
  }

  set user(value: AuthUser | null) {
    this._user = value;
  }

  private readUserSnapshotFromStorage(): AuthUser | null {
    const shared = this.storage.get<SharedUserSnapshot>(AUTH_CONSTS.user);
    if (!shared) return null;
    return this.mergeUserWithTabContext(shared);
  }

  private writeUserSnapshotToStorage(user: AuthUser | null): void {
    if (!user) {
      this.storage.remove(AUTH_CONSTS.user);
      return;
    }

    this.storage.set(AUTH_CONSTS.user, this.buildSharedUserSnapshot(user));
  }

  private buildSharedUserSnapshot(user: AuthUser): SharedUserSnapshot {
    return {
      id: user.id,
      name: user.name,
      surname: user.surname,
      email: user.email,
      locations: user.locations,
      avatar: user.avatar,
      status: user.status,
      global_status: user.global_status,
      phone: user.phone,
      location_phone_1: user.location_phone_1,
      location_phone_2: user.location_phone_2,
      electronic_signature: user.electronic_signature,
      user_type: user.user_type,
      global_roles: user.global_roles,
    };
  }

  private mergeUserWithTabContext(shared: SharedUserSnapshot): AuthUser {
    return {
      ...shared,
      location_id: this.locationContext.getCurrentLocationId() ?? undefined,
      is_admin_view: this.locationContext.isAdminViewMode(),
      roles: this.locationContext.getRoles(),
      permissions: this.locationContext.getPermissions(),
    };
  }

  // Observable for components to subscribe to user changes
  private userSubject = new BehaviorSubject<AuthUser | null>(null);
  public user$ = this.userSubject.asObservable().pipe(
    distinctUntilChanged((prev, curr) => {
      if (prev === null && curr === null) return true;
      if (prev === null || curr === null) return false;
      return this.hasSameUserContext(prev, curr);
    }),
  );

  setUserAndEmit(user: AuthUser | null) {
    this.user = user;
    this.writeUserSnapshotToStorage(user);
    if (user) {
      this.locationContext.setRolesAndPermissions(
        user.roles ?? [],
        user.permissions ?? [],
      );
    }
    this.userSubject.next(user);
  }

  private hasSameUserContext(previousUser: AuthUser, currentUser: AuthUser): boolean {
    return (
      previousUser.id === currentUser.id &&
      previousUser.location_id === currentUser.location_id &&
      previousUser.is_admin_view === currentUser.is_admin_view &&
      this.areStringArraysEqual(previousUser.roles ?? [], currentUser.roles ?? []) &&
      this.areStringArraysEqual(previousUser.permissions ?? [], currentUser.permissions ?? [])
    );
  }

  private areStringArraysEqual(previousValues: string[], currentValues: string[]): boolean {
    if (previousValues.length !== currentValues.length) {
      return false;
    }

    return previousValues.every((value, index) => value === currentValues[index]);
  }

  private getExpiresInMinutes(response: TwoFactorResendResponse): number {
    return response.expires_in_minutes ?? 10;
  }

  private clearPendingLoginState() {
    this.storage.remove(AUTH_CONSTS.tempAuthToken);
    this.storage.remove(AUTH_CONSTS.pendingLocationSelection);
    this.storage.remove(AUTH_CONSTS.pendingLocations);
    this.storage.remove(AUTH_CONSTS.pendingUser);
  }

  private clearSessionDeadline() {
    this.storage.remove(AUTH_CONSTS.sessionDeadlineAt);
  }

  private seedSessionDeadline(referenceTimeMs: number) {
    this.storage.set(
      AUTH_CONSTS.sessionDeadlineAt,
      referenceTimeMs + this.IDLE_TIMEOUT_FALLBACK_MS,
    );
  }

  private getSessionDeadlineFromStorage(): number | null {
    const sessionDeadlineAt = this.storage.get<number>(AUTH_CONSTS.sessionDeadlineAt);

    return Number.isFinite(sessionDeadlineAt) ? (sessionDeadlineAt as number) : null;
  }

  constructor() {
    this.setupSyncChannel();
    this.setupIdleDetection();
  }

  private setupSyncChannel() {
    if (typeof BroadcastChannel === 'undefined') return;

    this.syncChannel = new BroadcastChannel(this.SYNC_CHANNEL);
    this.syncChannel.onmessage = (event: MessageEvent<AuthSyncMessage>) => {
      const message = event.data;

      switch (message.type) {
        case 'SESSION_UPDATED':
          this.applySyncedSession(message.payload);
          if (message.payload.source === 'refresh') {
            this.resolvePendingRefreshWaiters(true);
          }
          break;
        case 'ACTIVITY':
          this.handleRemoteActivity(message.payload.at);
          break;
        case 'REFRESH_FAILED':
          this.releaseRefreshLock(message.payload.owner);
          this.resolvePendingRefreshWaiters(false);
          break;
        case 'LOGOUT':
          this.closeInactivityModal();
          this.resolvePendingRefreshWaiters(false);
          this.logout(true);
          this.router.navigate([AppRoutes.auth.login]);
          break;
      }
    };
  }

  private setupIdleDetection() {
    this.setupSessionLifecycleListeners();
    this.resetIdleTimer();
  }

  private setupSessionLifecycleListeners() {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return;
    }

    const handleUserActivity = () => {
      this.handleLocalActivity();
    };

    const handleSessionResume = () => {
      if (document.visibilityState !== 'visible') {
        return;
      }

      this.handleSessionResume();
    };

    document.addEventListener('visibilitychange', handleSessionResume);
    window.addEventListener('focus', handleSessionResume);
    window.addEventListener('pageshow', handleSessionResume);
    document.addEventListener('pointerdown', handleUserActivity);
    document.addEventListener('keydown', handleUserActivity);
    document.addEventListener('touchstart', handleUserActivity, { passive: true });
  }

  private resetIdleTimer() {
    if (this.idleTimer) {
      clearTimeout(this.idleTimer);
      this.idleTimer = null;
    }

    // Only set timer if user is authenticated
    if (this.authStateSubject.value === 'authenticated') {
      const remainingMs =
        this.currentIdleTimeoutMs - (Date.now() - this.lastActivityAt);
      if (remainingMs <= 0) {
        this.logout();
        this.router.navigate([AppRoutes.auth.login]);
        return;
      }

      const warningMs = this.getInactivityWarningMs();
      const msUntilWarning = remainingMs - warningMs;

      if (msUntilWarning <= 0) {
        this.onIdleDetected(remainingMs);
        return;
      }

      this.idleTimer = setTimeout(
        () => this.onIdleDetected(warningMs),
        msUntilWarning,
      );
    }
  }

  private getInactivityWarningMs(): number {
    return Math.min(
      this.INACTIVITY_WARNING_MS,
      Math.max(this.currentIdleTimeoutMs - 1000, 1000),
    );
  }

  private getRemainingIdleMs(): number {
    return this.currentIdleTimeoutMs - (Date.now() - this.lastActivityAt);
  }

  private handleSessionResume() {
    if (this.authStateSubject.value !== 'authenticated') {
      return;
    }

    const remainingMs = this.getRemainingIdleMs();

    if (remainingMs <= 0) {
      this.closeInactivityModal();
      this.logout();
      this.router.navigate([AppRoutes.auth.login]);
      return;
    }

    if (this.isInactivityDialogOpen()) {
      return;
    }

    this.handleLocalActivity();
  }

  private isInactivityDialogOpen(): boolean {
    return !!this.inactivityDialogRef;
  }

  private handleLocalActivity(shouldBroadcast = true) {
    if (this.authStateSubject.value !== 'authenticated' || this.isInactivityDialogOpen()) {
      return;
    }

    const activityAt = Date.now();
    this.lastActivityAt = activityAt;

    this.resetIdleTimer();

    if (
      !shouldBroadcast ||
      activityAt - this.lastActivityBroadcastAt < this.ACTIVITY_SYNC_THROTTLE_MS
    ) {
      return;
    }

    this.lastActivityBroadcastAt = activityAt;
    this.syncChannel?.postMessage({
      type: 'ACTIVITY',
      payload: { at: activityAt },
    } satisfies AuthSyncMessage);
  }

  private handleRemoteActivity(activityAt: number) {
    if (this.isInactivityDialogOpen()) {
      return;
    }

    if (activityAt <= this.lastActivityAt) {
      return;
    }

    this.lastActivityAt = activityAt;
    this.closeInactivityModal();
    this.resetIdleTimer();
  }

  private onIdleDetected(countdownMs = this.getInactivityWarningMs()) {
    if (
      this.inactivityDialogRef ||
      this.authStateSubject.value !== 'authenticated'
    ) {
      return;
    }

    this.inactivityDialogRef = this.dialog.open(InactivityModalComponent, {
      width: '400px',
      disableClose: true,
      data: {
        countdownSeconds: Math.max(1, Math.ceil(countdownMs / 1000)),
        expiresAtMs: Date.now() + Math.max(0, countdownMs),
      },
    });

    this.inactivityDialogRef.afterClosed().subscribe((result) => {
      const wasProgrammaticClose = this.suppressInactivityDialogCloseHandling;
      this.suppressInactivityDialogCloseHandling = false;
      this.inactivityDialogRef = null;

      if (result === 'refresh') {
        this.refreshToken().subscribe((success) => {
          if (success) {
            return;
          }

          this.logout();
          this.router.navigate([AppRoutes.auth.login]);
        });
      } else if (result === 'logout') {
        this.logout();
        this.router.navigate([AppRoutes.auth.login]);
      } else if (
        !wasProgrammaticClose &&
        this.authStateSubject.value === 'authenticated'
      ) {
        this.handleUnexpectedInactivityModalClose();
      }
    });
  }

  private handleUnexpectedInactivityModalClose() {
    const remainingMs = this.getRemainingIdleMs();

    if (remainingMs <= 0) {
      this.logout();
      this.router.navigate([AppRoutes.auth.login]);
      return;
    }

    if (remainingMs > this.getInactivityWarningMs()) {
      this.resetIdleTimer();
      return;
    }

    this.onIdleDetected(remainingMs);
  }

  private closeInactivityModal() {
    if (!this.inactivityDialogRef) {
      return;
    }

    this.suppressInactivityDialogCloseHandling = true;
    this.inactivityDialogRef.close();
    this.inactivityDialogRef = null;
  }

  initializeUserSession(fromSync = false) {
    if (this.isInitializing && !fromSync) {
      return;
    }

    // If we already have user data in memory, don't initialize again
    if (this.user && this.hasInitialized) {
      this.authStateSubject.next('authenticated');
      return;
    }

    // Check if there's an active impersonation session and restore it
    if (this.tryRestoreImpersonationSession()) {
      return;
    }

    this.isInitializing = true;
    this.authStateSubject.next('loading'); // Set loading state
    // console.log('AuthService: initializeUserSession started');

    // Check if we have a token in storage
    this.token = this.storage.get<string>(AUTH_CONSTS.token);
    // console.log('AuthService: initializeUserSession - token found:', !!this.token);

    if (!this.token) {
      // No token, redirect to login
      // console.log('AuthService: No token found, redirecting to login');
      this.isInitializing = false;
      this.authStateSubject.next('unauthenticated');
      this.redirectToLogin();
      return;
    }

    const storedSessionDeadlineAt = this.getSessionDeadlineFromStorage();
    if (storedSessionDeadlineAt !== null && storedSessionDeadlineAt <= Date.now()) {
      this.isInitializing = false;
      this.authStateSubject.next('unauthenticated');
      this.redirectToLogin();
      return;
    }

    if (!this.user) {
      const storedUser = this.readUserSnapshotFromStorage();
      if (storedUser) {
        this.user = storedUser;
      }
    }

    // Try to refresh and get user data
    // console.log('AuthService: Attempting to refresh token...');
    this.refreshToken().subscribe({
      next: (success) => {
        // console.log('AuthService: Refresh token result:', success);
        this.isInitializing = false;
        this.hasInitialized = true;
        if (success) {
          this.authStateSubject.next('authenticated');
        } else {
          this.authStateSubject.next('unauthenticated');
          this.redirectToLogin();
        }
      },
      error: () => {
        // console.log('AuthService: Refresh token failed');
        this.isInitializing = false;
        this.authStateSubject.next('unauthenticated');
        this.redirectToLogin();
      },
    });
  }

  private redirectToLogin() {
    this.logout(false, true);
    this.router.navigate([AppRoutes.auth.login]);
  }

  /**
   * Attempts to restore an active impersonation session from sessionStorage.
   * Returns true if restoration was successful, false otherwise.
   */
  private tryRestoreImpersonationSession(): boolean {
    const impersonationStateJson = sessionStorage.getItem(
      IMPERSONATION_CONSTS.state,
    );
    if (!impersonationStateJson) {
      return false;
    }

    try {
      const { expiresAt, impersonatedUser } = JSON.parse(
        impersonationStateJson,
      );

      const isExpired = !expiresAt || new Date(expiresAt) <= new Date();
      const hasAdminToken = !!this.storage.get<string>(AUTH_CONSTS.token);

      if (isExpired || !hasAdminToken || !impersonatedUser) {
        return false;
      }

      if (!this.user) {
        this.user = { ...impersonatedUser };
        this.userSubject.next(this.user);
      }

      this.hasInitialized = true;
      this.authStateSubject.next('authenticated');
      return true;
    } catch {
      return false;
    }
  }

  getUser<T extends AuthUser>(): T {
    return this.user as T;
  }

  getUserFromStorage<T extends AuthUser>() {
    // Keep the token in sync and restore the latest stored user snapshot when needed.
    this.token = this.storage.get<string>(AUTH_CONSTS.token);
    if (!this.user) {
      this.user = this.readUserSnapshotFromStorage();
    }

    return this.user as T;
  }

  public getTokenFromStorage(): string {
    this.token = this.storage.get<string>(AUTH_CONSTS.token);
    return this.token ?? '';
  }

  public getRefreshTokenFromStorage(): string {
    return this.storage.get<string>(AUTH_CONSTS.refreshToken) ?? '';
  }

  public updateSessionTimeoutFromHeaders(
    idleSecondsHeader: string | null,
    timeoutSecondsHeader: string | null,
  ) {
    const idleSeconds = Number(idleSecondsHeader);
    const timeoutSeconds = Number(timeoutSecondsHeader);

    if (!Number.isFinite(timeoutSeconds) || timeoutSeconds <= 0) {
      return;
    }

    if (this.isInactivityDialogOpen()) {
      return;
    }

    this.currentIdleTimeoutMs = timeoutSeconds * 1000;

    if (Number.isFinite(idleSeconds) && idleSeconds >= 0) {
      const sessionDeadlineAt = Date.now() + (timeoutSeconds - idleSeconds) * 1000;
      this.storage.set(AUTH_CONSTS.sessionDeadlineAt, sessionDeadlineAt);
    }

    if (Number.isFinite(idleSeconds) && idleSeconds >= 0) {
      const computedLastActivityAt = Date.now() - idleSeconds * 1000;
      this.lastActivityAt = Math.max(0, computedLastActivityAt);

      if (
        this.authStateSubject.value === 'authenticated' &&
        this.lastActivityAt - this.lastActivityBroadcastAt >=
          this.ACTIVITY_SYNC_THROTTLE_MS
      ) {
        this.lastActivityBroadcastAt = this.lastActivityAt;
        this.syncChannel?.postMessage({
          type: 'ACTIVITY',
          payload: { at: this.lastActivityAt },
        } satisfies AuthSyncMessage);
      }
    }

    this.resetIdleTimer();
  }

  private updateLocationContextFromUser(user: AuthUser) {
    const isAdminView = !!user.is_admin_view;
    this.locationContext.setAdminViewMode(isAdminView);

    if (isAdminView) {
      this.locationContext.clearCurrentLocationId();
    } else if (typeof user.location_id === 'number') {
      this.locationContext.setCurrentLocationId(user.location_id);
    }

    this.locationContext.setRolesAndPermissions(
      user.roles ?? [],
      user.permissions ?? [],
    );
  }

  private persistAuthenticatedSession(
    accessToken: string,
    user: AuthUser,
    refreshToken: string,
    source: SyncSessionSource,
    shouldBroadcast = true,
  ) {
    this.storage.set(AUTH_CONSTS.token, accessToken);
    this.storage.set(AUTH_CONSTS.refreshToken, refreshToken);
    this.storage.set(AUTH_CONSTS.auth, 'true');
    this.writeUserSnapshotToStorage(user);

    this.user = user;
    this.token = accessToken;
    this.hasInitialized = true;
    this.currentIdleTimeoutMs = this.IDLE_TIMEOUT_FALLBACK_MS;
    this.lastActivityAt = Date.now();
    this.seedSessionDeadline(this.lastActivityAt);

    this.userSubject.next(this.user);
    this.authStateSubject.next('authenticated');
    this.updateLocationContextFromUser(user);

    if (typeof this.featureFlags.loadFlags === 'function') {
      this.featureFlags.loadFlags().subscribe();
    }
    this.resetIdleTimer();

    if (shouldBroadcast) {
      this.syncChannel?.postMessage({
        type: 'SESSION_UPDATED',
        payload: {
          accessToken,
          refreshToken,
          user: this.buildSharedUserSnapshot(user),
          activityAt: this.lastActivityAt,
          source,
        },
      } satisfies AuthSyncMessage);
    }
  }

  private applySyncedSession(payload: SyncSessionPayload) {
    // Update shared session data in localStorage (tokens + base user snapshot)
    this.storage.set(AUTH_CONSTS.token, payload.accessToken);
    this.storage.set(AUTH_CONSTS.refreshToken, payload.refreshToken);
    this.storage.set(AUTH_CONSTS.auth, 'true');
    this.storage.set(AUTH_CONSTS.user, payload.user);

    this.token = payload.accessToken;
    this.hasInitialized = true;
    this.currentIdleTimeoutMs = this.IDLE_TIMEOUT_FALLBACK_MS;
    this.lastActivityAt = payload.activityAt;
    this.seedSessionDeadline(this.lastActivityAt);

    // Merge shared user data with THIS tab's existing location context.
    // This preserves the receiving tab's location_id, roles, permissions.
    const mergedUser = this.mergeUserWithTabContext(payload.user);
    this.user = mergedUser;
    this.userSubject.next(mergedUser);
    this.authStateSubject.next('authenticated');

    // Do NOT call updateLocationContextFromUser — preserve this tab's context.

    if (typeof this.featureFlags.loadFlags === 'function') {
      this.featureFlags.loadFlags().subscribe();
    }
    this.closeInactivityModal();
    this.resetIdleTimer();
  }

  private readRefreshLock(): RefreshLockState | null {
    const raw = localStorage.getItem(this.REFRESH_LOCK_KEY);
    if (!raw) {
      return null;
    }

    try {
      const parsed = JSON.parse(raw) as RefreshLockState;
      if (!parsed.owner || !parsed.expiresAt) {
        return null;
      }

      return parsed;
    } catch {
      return null;
    }
  }

  private acquireRefreshLock(): boolean {
    const now = Date.now();
    const currentLock = this.readRefreshLock();

    if (
      currentLock &&
      currentLock.expiresAt > now &&
      currentLock.owner !== this.instanceId
    ) {
      return false;
    }

    const nextLock: RefreshLockState = {
      owner: this.instanceId,
      expiresAt: now + this.REFRESH_LOCK_TTL_MS,
    };

    localStorage.setItem(this.REFRESH_LOCK_KEY, JSON.stringify(nextLock));

    return this.readRefreshLock()?.owner === this.instanceId;
  }

  private releaseRefreshLock(owner = this.instanceId) {
    const currentLock = this.readRefreshLock();

    if (currentLock?.owner === owner) {
      localStorage.removeItem(this.REFRESH_LOCK_KEY);
    }
  }

  private waitForCrossTabRefresh(): Observable<boolean> {
    return new Observable<boolean>((subscriber) => {
      const resolver = (success: boolean) => {
        clearTimeout(timeoutId);
        subscriber.next(success);
        subscriber.complete();
      };

      const timeoutId = setTimeout(() => {
        this.pendingRefreshResolvers = this.pendingRefreshResolvers.filter(
          (item) => item !== resolver,
        );
        subscriber.next(false);
        subscriber.complete();
      }, this.REFRESH_WAIT_TIMEOUT_MS);

      this.pendingRefreshResolvers.push(resolver);

      return () => {
        clearTimeout(timeoutId);
        this.pendingRefreshResolvers = this.pendingRefreshResolvers.filter(
          (item) => item !== resolver,
        );
      };
    });
  }

  private resolvePendingRefreshWaiters(success: boolean) {
    const resolvers = [...this.pendingRefreshResolvers];
    this.pendingRefreshResolvers = [];
    resolvers.forEach((resolver) => resolver(success));
  }

  setUserToStorage(auth: Auth): boolean {
    if (!auth) {
      return false;
    }

    // IMPORTANT: Clear any residual impersonation state
    // This prevents issues if a previous session expired during impersonation
    sessionStorage.removeItem(IMPERSONATION_CONSTS.token);
    sessionStorage.removeItem(IMPERSONATION_CONSTS.state);

    let token: string;

    if (typeof auth.access_token === 'string') {
      token = auth.access_token;
    } else if (auth.access_token?.original?.access_token) {
      token = auth.access_token.original.access_token;
    } else {
      return false;
    }

    if (!token || !auth.user) {
      return false;
    }

    if (!auth.refresh_token) {
      return false;
    }

    this.clearPendingLoginState();

    this.persistAuthenticatedSession(
      token,
      auth.user,
      auth.refresh_token,
      'login',
    );

    return true;
  }

  setUserFromRefresh(refreshResponse: RefreshResponse): boolean {
    if (!refreshResponse?.access_token || !refreshResponse.refresh_token) {
      return false;
    }

    if (!refreshResponse.user) {
      if (!this.user) {
        return false;
      }

      this.persistAuthenticatedSession(
        refreshResponse.access_token,
        this.user,
        refreshResponse.refresh_token,
        'refresh',
      );

      this.clearPendingLoginState();

      return true;
    }

    const normalizedLocations = this.appendAdminViewLocationIfAuthorized(
      refreshResponse.user.locations || [],
      refreshResponse.user.permissions || [],
    );

    // Preserve this tab's location context during refresh.
    // If the tab already has context (e.g. user changed location), keep it.
    // If the tab has no context (new tab, empty sessionStorage), fall back to refresh response.
    const tabRoles = this.locationContext.getRoles();
    const tabPermissions = this.locationContext.getPermissions();
    const tabHasContext = this.locationContext.hasTabContext();

    const nextUser: AuthUser = {
      id: refreshResponse.user.id,
      name: refreshResponse.user.name,
      surname: refreshResponse.user.surname,
      email: refreshResponse.user.email,
      permissions: tabHasContext
        ? tabPermissions
        : (refreshResponse.user.permissions || []),
      locations: normalizedLocations.map((location) => ({
        id: location.id,
        title: location.title,
        avatar: StringOrNullOrUndefined(location.avatar),
        city: StringOrNullOrUndefined(location.city),
        status: StringOrNullOrUndefined(location.status),
      })),
      roles: tabHasContext
        ? tabRoles
        : (refreshResponse.user.roles || []),
      location_id: tabHasContext
        ? (this.locationContext.getCurrentLocationId() ?? undefined)
        : (refreshResponse.user.location_id || normalizedLocations?.[0]?.id || 0),
      is_admin_view: tabHasContext
        ? this.locationContext.isAdminViewMode()
        : (refreshResponse.user.is_admin_view || false),
      avatar: refreshResponse.user.avatar || undefined,
      status: refreshResponse.user.status || 'active',
      global_status:
        refreshResponse.user.global_status ||
        refreshResponse.user.status ||
        'active',
      phone: refreshResponse.user.phone || undefined,
      location_phone_1: refreshResponse.user.location_phone_1 ?? this.user?.location_phone_1,
      location_phone_2: refreshResponse.user.location_phone_2 ?? this.user?.location_phone_2,
      user_type: this.user?.user_type ?? (() => {
          throw new Error('User type missing during refresh');
        })(),
    };

    this.persistAuthenticatedSession(
      refreshResponse.access_token,
      nextUser,
      refreshResponse.refresh_token,
      'refresh',
    );

    this.clearPendingLoginState();

    return true;
  }

  login(
    identifier: string,
    password: string,
    parent: boolean = false,
  ): Observable<LoginResult> {
    const loginUrl = parent ? AUTH_URLS.loginParent : AUTH_URLS.login;
    const body = { identifier, password };

    return this.http
      .post<LoginResponse>(loginUrl, body)
      .pipe(
        map((response): LoginResult => {
          // Check if 2FA is required
          if (response.two_factor_required) {
            this.storage.set('two_factor_email', identifier);

            // NEW: Check if method selection is required (new flow)
            if (response.method_selection_required) {
              // Don't set expiration yet - user hasn't selected method
              this.updateTwoFactorState({
                required: true,
                expiresAt: null, // No expiration until method is selected
                attemptsRemaining: response.attempts_remaining ?? 5,
                isVerifying: false,
                error: null,
                developmentMode: response.development_mode ?? false,
                smsServiceActive: response.sms_service_active ?? false,
                availableMethods: response.available_methods ?? [],
              });
              return {
                success: false as const,
                requiresTwoFactor: true as const,
                methodSelectionRequired: true as const,
                availableMethods: response.available_methods ?? [],
                phoneHint: response.phone_hint,
                emailHint: response.email_hint,
              };
            }

            const expiresAt = response.expires_in_minutes
              ? new Date(
                  Date.now() + response.expires_in_minutes * 60 * 1000,
                ).toISOString()
              : null;

            this.updateTwoFactorState({
              required: true,
              expiresAt: expiresAt,
              attemptsRemaining: response.attempts_remaining ?? 5,
              isVerifying: false,
              error: null,
              developmentMode: response.development_mode ?? false,
              smsServiceActive: response.sms_service_active ?? false,
            });

            return {
              success: false as const,
              requiresTwoFactor: true as const,
              methodSelectionRequired: false as const,
            };
          }

          // Check if location selection is required
          if (response.location_selection_required && response.locations) {
            // Store JWT token temporarily (will be used in selectLocation request)
            this.storage.set(AUTH_CONSTS.tempAuthToken, response.access_token);

            // Store locations for the dialog
            this.storage.set(AUTH_CONSTS.pendingLocationSelection, true);
            this.storage.set(AUTH_CONSTS.pendingLocations, response.locations);
            this.storage.set(AUTH_CONSTS.pendingUser, response.user);

            return {
              success: true as const,
              requiresTwoFactor: false as const,
              requiresLocationSelection: true as const,
            };
          }

          // Normal login flow - convert LoginResponse to Auth for compatibility
          if (response.access_token && response.user) {
            this.clearPendingLoginState();

            const authResponse: Auth = {
              access_token: response.access_token,
              refresh_token: response.refresh_token,
              user: response.user,
            };

            const stored = this.setUserToStorage(authResponse);
            if (stored) {
              return {
                success: true as const,
                requiresTwoFactor: false as const,
              };
            }
            return {
              success: false as const,
              requiresTwoFactor: false as const,
            };
          }

          return { success: false as const, requiresTwoFactor: false as const };
        }),
        tap((result) => {
          if (result.success && !('requiresLocationSelection' in result)) {
            this.getUserFromStorage();
          }
        }),
        catchError((error: HttpErrorResponse): Observable<LoginResult> => {
          if (error.status === 202 && error.error?.two_factor_required) {
            this.storage.set('two_factor_email', identifier);

            // NEW: Check if method selection is required
            if (error.error.method_selection_required) {
              this.updateTwoFactorState({
                required: true,
                expiresAt: null,
                attemptsRemaining: error.error.attempts_remaining ?? 5,
                isVerifying: false,
                error: null,
                developmentMode: error.error.development_mode ?? false,
                smsServiceActive: error.error.sms_service_active ?? false,
                availableMethods: error.error.available_methods ?? [],
              });

              return of<LoginResult>({
                success: false as const,
                requiresTwoFactor: true as const,
                methodSelectionRequired: true as const,
                availableMethods: error.error.available_methods ?? [],
                phoneHint: error.error.phone_hint,
                emailHint: error.error.email_hint,
              });
            }

            const expiresAt = error.error.expires_in_minutes
              ? new Date(
                  Date.now() + error.error.expires_in_minutes * 60 * 1000,
                ).toISOString()
              : null;

            this.updateTwoFactorState({
              required: true,
              expiresAt: expiresAt,
              attemptsRemaining: error.error.attempts_remaining ?? 5,
              isVerifying: false,
              error: null,
              developmentMode: error.error.development_mode ?? false,
              smsServiceActive: error.error.sms_service_active ?? false,
            });

            return of<LoginResult>({
              success: false as const,
              requiresTwoFactor: true as const,
              methodSelectionRequired: false as const,
            });
          }

          if (
            error.status === 409 &&
            error.error?.error === AuthErrorCode.AmbiguousParentLocation &&
            Array.isArray(error.error?.locations)
          ) {
            return of<LoginResult>({
              success: false as const,
              requiresTwoFactor: false as const,
              requiresParentLocationSelection: true as const,
              locations: error.error.locations,
            });
          }

          throw error.error;
        }),
      );
  }

  refreshToken() {
    const refreshToken = this.getRefreshTokenFromStorage();

    if (!refreshToken) {
      return of(false);
    }

    if (!this.acquireRefreshLock()) {
      return this.waitForCrossTabRefresh();
    }

    return this.http
      .get<RefreshResponse>(AUTH_URLS.refresh, {
        headers: {
          'X-Refresh-Token': refreshToken,
        },
      })
      .pipe(
        map((refreshResponse) => {
          return this.setUserFromRefresh(refreshResponse);
        }),
        catchError((error) => {
          this.syncChannel?.postMessage({
            type: 'REFRESH_FAILED',
            payload: { owner: this.instanceId },
          } satisfies AuthSyncMessage);

          // If refresh fails, clear storage and return false
          console.log('AuthService: refresh token failed with error:', error);
          this.logout(false, true);
          return of(false);
        }),
        finalize(() => {
          this.releaseRefreshLock();
        }),
      );
  }

  private clearLocalSession(fromSync = false) {
    if (this.idleTimer) {
      clearTimeout(this.idleTimer);
      this.idleTimer = null;
    }

    this.closeInactivityModal();
    this.releaseRefreshLock();
    this.currentIdleTimeoutMs = this.IDLE_TIMEOUT_FALLBACK_MS;
    this.lastActivityAt = Date.now();
    this.lastActivityBroadcastAt = 0;

    // Clear storage
    this.storage.remove(AUTH_CONSTS.token);
    this.storage.remove(AUTH_CONSTS.refreshToken);
    this.storage.remove(AUTH_CONSTS.user);
    this.storage.remove(AUTH_CONSTS.auth);
    this.clearSessionDeadline();
    localStorage.removeItem('oldDataKey');

    // IMPORTANT: Clear impersonation state from sessionStorage
    // This prevents issues when token expires during impersonation
    sessionStorage.removeItem(IMPERSONATION_CONSTS.token);
    sessionStorage.removeItem(IMPERSONATION_CONSTS.state);

    // Clear 2FA state
    this.clearTwoFactorStateOnLogout();

    // Clear per-tab location context (sessionStorage + BehaviorSubjects)
    this.locationContext.clearAll();
    this._user = null;
    this.token = null;
    this.isInitializing = false; // Reset initialization flag
    this.hasInitialized = false; // Reset initialization flag

    // Emit user change and auth state
    // console.log('AuthService: Emitting null user on logout');
    this.userSubject.next(null);
    this.authStateSubject.next('unauthenticated');
    this.resolvePendingRefreshWaiters(false);

    // Notify other tabs
    if (!fromSync) {
      this.syncChannel?.postMessage({
        type: 'LOGOUT',
      } satisfies AuthSyncMessage);
    }

    return of(null);
  }

  logout(fromSync = false, skipBackendLogout = false) {
    const accessToken = this.getTokenFromStorage();
    const refreshToken = this.getRefreshTokenFromStorage();

    this.clearLocalSession(fromSync);

    if (fromSync || skipBackendLogout || !accessToken || !refreshToken) {
      return of(null);
    }

    return this.http.post(
      AUTH_URLS.logout,
      { refresh_token: refreshToken },
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      }
    ).pipe(
      map(() => null),
      catchError((error: HttpErrorResponse) => {
        console.log('[AUTH] Remote logout failed:', error);
        return of(null);
      }),
    );
  }

  getUserRomoto<T>(data: unknown) {
    return this.http.post<T>(AUTH_URLS.me, data);
  }

  isAuthenticated(): boolean {
    const token = this.storage.get<string>(AUTH_CONSTS.token);
    // console.log('AuthService: isAuthenticated check - token exists:', !!token);
    return !!token;
  }

  refreshPermissions(): Observable<boolean> {
    return this.http.post<AuthUser>(AUTH_URLS.me, {}).pipe(
      map((meResponse) => {
        if (!meResponse || !this.user) {
          return false;
        }
        const updatedUser: AuthUser = {
          ...this.user,
          permissions: meResponse.permissions ?? this.user.permissions,
          roles: meResponse.roles ?? this.user.roles,
        };
        this.writeUserSnapshotToStorage(updatedUser);
        this.user = updatedUser;
        this.locationContext.setRolesAndPermissions(
          updatedUser.roles ?? [],
          updatedUser.permissions ?? [],
        );
        this.userSubject.next(updatedUser);
        return true;
      }),
      catchError(() => of(false)),
    );
  }

  // Method to manually trigger user session initialization
  // Useful for cases where we need to reload user data
  public reinitializeUserSession() {
    this.initializeUserSession();
  }

  // Two Factor Authentication Methods

  sendTwoFactorCode(method: TwoFactorMethod, context: 'login' | 'password_reset' = 'login') {
    const email = this.storage.get<string>('two_factor_email');

    if (!email) {
      const error = 'Email not found for 2FA send code';
      this.updateTwoFactorState({
        ...this.twoFactorStateSubject.value,
        error: error,
      });
      throw { error: { message: error } };
    }

    const request: TwoFactorSendCodeRequest = { email, method, context };

    return this.http
      .post<TwoFactorSendCodeResponse>(AUTH_URLS.twoFactorSendCode, request)
      .pipe(
        tap((response) => {
          const expiresInMinutes = response.expires_in_minutes || 10;
          const expiresAt = new Date(
            Date.now() + expiresInMinutes * 60 * 1000,
          ).toISOString();
          const currentState = this.twoFactorStateSubject.value;

          const newState = {
            ...currentState,
            required: true,
            expiresAt: expiresAt,
            attemptsRemaining: response.attempts_remaining ?? 5,
            error: null,
            developmentMode: response.development_mode || false,
            smsServiceActive: response.sms_service_active || false,
          };

          this.updateTwoFactorState(newState);
        }),
        catchError((error: HttpErrorResponse) => {
          this.updateTwoFactorState({
            ...this.twoFactorStateSubject.value,
            error: error.error?.message || 'Failed to send verification code',
          });

          throw error.error;
        }),
      );
  }

  verifyTwoFactorCode(code: string) {
    this.updateTwoFactorState({
      ...this.twoFactorStateSubject.value,
      isVerifying: true,
      error: null,
    });

    const email = this.storage.get<string>('two_factor_email');

    if (!email) {
      const error = 'Email not found for 2FA verification';
      this.updateTwoFactorState({
        ...this.twoFactorStateSubject.value,
        isVerifying: false,
        error: error,
      });
      throw { error: { message: error } };
    }

    const request: TwoFactorVerificationRequest = { code, email };

    return this.http
      .post<TwoFactorResponse>(AUTH_URLS.twoFactorVerify, request)
      .pipe(
        map((response) => {
          // Check if location selection is required
          if (response.location_selection_required && response.locations) {
            // Store JWT token temporarily (will be used in selectLocation request)
            this.storage.set(AUTH_CONSTS.tempAuthToken, response.access_token);

            // Store locations for the dialog
            this.storage.set(AUTH_CONSTS.pendingLocationSelection, true);
            this.storage.set(AUTH_CONSTS.pendingLocations, response.locations);
            this.storage.set(AUTH_CONSTS.pendingUser, response.user);

            // Clear 2FA state
            this.clearTwoFactorState();
            this.storage.remove('two_factor_email');

            // Return a special success flag indicating location selection needed
            return { success: true, requiresLocationSelection: true };
          }

          // Normal flow - access_token is present (for parents)
          if (!response.access_token) {
            return { success: false };
          }

          this.clearPendingLoginState();

          const authResponse: Auth = {
            access_token: response.access_token,
            refresh_token: response.refresh_token,
            user: response.user,
          };

          const success = this.setUserToStorage(authResponse);

          if (success) {
            // Clear 2FA state and email storage on successful verification
            this.clearTwoFactorState();
            this.storage.remove('two_factor_email');
          }

          return { success };
        }),
        tap(
          (result: {
            success: boolean;
            requiresLocationSelection?: boolean;
          }) => {
            if (result.success && !result.requiresLocationSelection) {
              this.getUserFromStorage();
            }
          },
        ),
        catchError((error: HttpErrorResponse) => {
          // Update state with error
          const currentState = this.twoFactorStateSubject.value;

          // Handle specific error cases
          if (error.error?.code_expired) {
            // Code expired - clear the state and show specific message
            this.updateTwoFactorState({
              required: true,
              expiresAt: null,
              attemptsRemaining: error.error.attempts_remaining ?? 5,
              isVerifying: false,
              error: 'Verification code has expired. Please request a new one.',
              developmentMode: currentState.developmentMode,
              smsServiceActive: currentState.smsServiceActive,
            });
          } else {
            // Regular verification error
            this.updateTwoFactorState({
              ...currentState,
              isVerifying: false,
              error: error.error?.message || 'Verification failed',
              attemptsRemaining: Math.max(
                0,
                currentState.attemptsRemaining - 1,
              ),
            });
          }

          throw error.error;
        }),
      );
  }

  resendTwoFactorCode(context: 'login' | 'password_reset' = 'login') {
    const email = this.storage.get<string>('two_factor_email');

    if (!email) {
      const error = 'Email not found for 2FA resend';
      this.updateTwoFactorState({
        ...this.twoFactorStateSubject.value,
        error: error,
      });
      throw { error: { message: error } };
    }

    return this.http
      .post<TwoFactorResendResponse>(AUTH_URLS.twoFactorResend, { email, context })
      .pipe(
        tap((response) => {
          // SIEMPRE calcular expires_at localmente para evitar problemas de zona horaria
          const expiresInMinutes = this.getExpiresInMinutes(response);
          const expiresAt = new Date(
            Date.now() + expiresInMinutes * 60 * 1000,
          ).toISOString();
          console.log(
            'AuthService: resendSMS - calculated expires_at locally:',
            {
              expires_in_minutes: expiresInMinutes,
              calculated_expires_at: expiresAt,
              current_time: new Date().toISOString(),
            },
          );

          const currentState = this.twoFactorStateSubject.value;
          this.updateTwoFactorState({
            ...currentState,
            expiresAt: expiresAt,
            attemptsRemaining: response.attempts_remaining ?? 5, // Reset on resend
            error: null,
            developmentMode: response.development_mode || false,
            smsServiceActive: response.sms_service_active || false,
          });
        }),
        catchError((error: HttpErrorResponse) => {
          this.updateTwoFactorState({
            ...this.twoFactorStateSubject.value,
            error: error.error?.message || 'Failed to resend code',
          });

          throw error.error;
        }),
      );
  }

  resendTwoFactorCodeViaEmail(context: 'login' | 'password_reset' = 'login') {
    const email = this.storage.get<string>('two_factor_email');

    if (!email) {
      const error = 'Email not found for 2FA resend';
      this.updateTwoFactorState({
        ...this.twoFactorStateSubject.value,
        error: error,
      });
      throw { error: { message: error } };
    }

    return this.http
      .post<TwoFactorResendResponse>(AUTH_URLS.twoFactorResend, {
        email: email,
        method: TwoFactorMethod.EMAIL,
        context,
      })
      .pipe(
        tap((response) => {
          // SIEMPRE calcular expires_at localmente para evitar problemas de zona horaria
          const expiresInMinutes = this.getExpiresInMinutes(response);
          const expiresAt = new Date(
            Date.now() + expiresInMinutes * 60 * 1000,
          ).toISOString();
          console.log(
            'AuthService: resendEmail - calculated expires_at locally:',
            {
              expires_in_minutes: expiresInMinutes,
              calculated_expires_at: expiresAt,
              current_time: new Date().toISOString(),
            },
          );

          const currentState = this.twoFactorStateSubject.value;
          this.updateTwoFactorState({
            ...currentState,
            expiresAt: expiresAt,
            attemptsRemaining: response.attempts_remaining ?? 5,
            error: null,
            developmentMode: response.development_mode || false,
            smsServiceActive: response.sms_service_active || false,
          });
        }),
        catchError((error: HttpErrorResponse) => {
          this.updateTwoFactorState({
            ...this.twoFactorStateSubject.value,
            error: error.error?.message || 'Failed to send email code',
          });

          throw error.error;
        }),
      );
  }

  verifyBackupCode(backupCode: string) {
    this.updateTwoFactorState({
      ...this.twoFactorStateSubject.value,
      isVerifying: true,
      error: null,
    });

    // Get email from storage
    const email = this.storage.get<string>('two_factor_email');

    if (!email) {
      const error = 'Email not found for backup code verification';
      this.updateTwoFactorState({
        ...this.twoFactorStateSubject.value,
        isVerifying: false,
        error: error,
      });
      throw { error: { message: error } };
    }

    const request: TwoFactorBackupCodeRequest = {
      backup_code: backupCode,
      email,
    };

    return this.http
      .post<TwoFactorResponse>(AUTH_URLS.twoFactorBackupCode, request)
      .pipe(
        map((response) => {
          // Check if location selection is required
          if (response.location_selection_required && response.locations) {
            // Store JWT token temporarily (will be used in selectLocation request)
            this.storage.set(AUTH_CONSTS.tempAuthToken, response.access_token);

            // Store locations for the dialog
            this.storage.set(AUTH_CONSTS.pendingLocationSelection, true);
            this.storage.set(AUTH_CONSTS.pendingLocations, response.locations);
            this.storage.set(AUTH_CONSTS.pendingUser, response.user);

            // Clear 2FA state
            this.clearTwoFactorState();
            this.storage.remove('two_factor_email');

            // Return a special success flag indicating location selection needed
            return { success: true, requiresLocationSelection: true };
          }

          // Normal flow - access_token is present (for parents)
          if (!response.access_token) {
            return { success: false };
          }

          this.clearPendingLoginState();

          const authResponse: Auth = {
            access_token: response.access_token,
            refresh_token: response.refresh_token,
            user: response.user,
          };

          const success = this.setUserToStorage(authResponse);

          if (success) {
            // Clear 2FA state and email storage on successful verification
            this.clearTwoFactorState();
            this.storage.remove('two_factor_email');
          }

          return { success };
        }),
        tap(
          (result: {
            success: boolean;
            requiresLocationSelection?: boolean;
          }) => {
            if (result.success && !result.requiresLocationSelection) {
              this.getUserFromStorage();
            }
          },
        ),
        catchError((error: HttpErrorResponse) => {
          // Update state with error
          const currentState = this.twoFactorStateSubject.value;
          this.updateTwoFactorState({
            ...currentState,
            isVerifying: false,
            error: error.error?.message || 'Backup code verification failed',
          });

          throw error.error;
        }),
      );
  }

  // Two Factor Authentication state management methods

  private updateTwoFactorState(state: TwoFactorState) {
    this.twoFactorStateSubject.next(state);

    // Persist 2FA state in storage during verification flow
    if (state.required) {
      this.storage.set(AUTH_CONSTS.twoFactorRequired, 'true');
      if (state.expiresAt) {
        this.storage.set(AUTH_CONSTS.twoFactorExpiresAt, state.expiresAt);
      }
      this.storage.set(AUTH_CONSTS.twoFactorAttemptsRemaining, state.attemptsRemaining);
      this.storage.set(AUTH_CONSTS.twoFactorAvailableMethods, state.availableMethods ?? []);
      this.storage.set(AUTH_CONSTS.twoFactorUserRole, state.userRole ?? null);
      // Store backend-dependent flags
      this.storage.set(
        AUTH_CONSTS.twoFactorDevelopmentMode,
        (state.developmentMode ?? false).toString(),
      );
      this.storage.set(
        AUTH_CONSTS.twoFactorSmsServiceActive,
        (state.smsServiceActive ?? false).toString(),
      );
    } else {
      this.storage.remove(AUTH_CONSTS.twoFactorRequired);
      this.storage.remove(AUTH_CONSTS.twoFactorExpiresAt);
      this.storage.remove(AUTH_CONSTS.twoFactorAttemptsRemaining);
      this.storage.remove(AUTH_CONSTS.twoFactorAvailableMethods);
      this.storage.remove(AUTH_CONSTS.twoFactorUserRole);
      this.storage.remove(AUTH_CONSTS.twoFactorDevelopmentMode);
      this.storage.remove(AUTH_CONSTS.twoFactorSmsServiceActive);
    }
  }

  private clearTwoFactorState() {
    this.twoFactorStateSubject.next({
      required: false,
      expiresAt: null,
      attemptsRemaining: 5, // Use config default
      isVerifying: false,
      error: null,
      developmentMode: false,
      smsServiceActive: false,
      userRole: null,
    });

    // Clear from storage
    this.storage.remove(AUTH_CONSTS.twoFactorRequired);
    this.storage.remove(AUTH_CONSTS.twoFactorExpiresAt);
    this.storage.remove(AUTH_CONSTS.twoFactorDevelopmentMode);
    this.storage.remove(AUTH_CONSTS.twoFactorSmsServiceActive);
  }

  setTwoFactorUserRole(role: 'parent' | 'employee' | null) {
    const currentState = this.twoFactorStateSubject.value;
    this.updateTwoFactorState({
      ...currentState,
      userRole: role,
    });
  }

  getCurrentTwoFactorState(): TwoFactorState {
    const currentState = this.twoFactorStateSubject.value;

    console.log('AuthService: getCurrentTwoFactorState called', {
      currentState: currentState,
      currentStateRequired: currentState.required,
    });

    // If not required in memory, check storage
    if (!currentState.required) {
      const storedRequired = this.storage.get<string>(
        AUTH_CONSTS.twoFactorRequired,
      );
      const storedExpiresAt = this.storage.get<string>(
        AUTH_CONSTS.twoFactorExpiresAt,
      );
      const storedDevelopmentMode = this.storage.get<string>(
        AUTH_CONSTS.twoFactorDevelopmentMode,
      );
      const storedSmsServiceActive = this.storage.get<string>(
        AUTH_CONSTS.twoFactorSmsServiceActive,
      );
      const storedAttemptsRemaining = this.storage.get<number>(
        AUTH_CONSTS.twoFactorAttemptsRemaining,
      );
      const storedAvailableMethods = this.storage.get<TwoFactorMethod[]>(
        AUTH_CONSTS.twoFactorAvailableMethods,
      );
      const storedUserRole = this.storage.get<'parent' | 'employee' | null>(
        AUTH_CONSTS.twoFactorUserRole,
      );

      console.log('AuthService: localStorage contents:', {
        storedRequired,
        storedExpiresAt,
        storedDevelopmentMode,
        storedSmsServiceActive,
        storedAttemptsRemaining,
        storedAvailableMethods,
        storedUserRole,
      });

      if (storedRequired?.toString() === 'true') {
        console.log(
          'AuthService: Found 2FA state in localStorage, restoring...',
        );

        const restoredState: TwoFactorState = {
          required: true,
          expiresAt: storedExpiresAt || null,
          attemptsRemaining:
            typeof storedAttemptsRemaining === 'number' ? storedAttemptsRemaining : 5,
          isVerifying: false,
          error: null,
          developmentMode: storedDevelopmentMode?.toString() === 'true',
          smsServiceActive: storedSmsServiceActive?.toString() === 'true',
          availableMethods: storedAvailableMethods ?? [],
          userRole: storedUserRole ?? null,
        };

        console.log('AuthService: Restored state:', restoredState);
        this.updateTwoFactorState(restoredState);
        return restoredState;
      } else {
        console.log(
          'AuthService: No 2FA state found in localStorage (storedRequired not "true")',
        );
      }
    } else {
      console.log(
        'AuthService: Current state already required, returning current state',
      );
    }

    console.log('AuthService: Returning current state:', currentState);
    return currentState;
  }

  // Get Two Factor status from backend
  getTwoFactorStatus() {
    return this.http
      .get<TwoFactorStatusResponse>(AUTH_URLS.twoFactorStatus)
      .pipe(
        tap((response) => {
          console.log('AuthService: 2FA status response received:', response);
        }),
        catchError((error: HttpErrorResponse) => {
          throw error.error;
        }),
      );
  }

  // Clear 2FA state when user logs out
  private clearTwoFactorStateOnLogout() {
    this.clearTwoFactorState();
    // Clear email storage
    this.storage.remove('two_factor_email');
    this.clearPendingLoginState();
  }

  can(permission: string): boolean {
    if (!this.user?.permissions) {
      return false;
    }
    return this.user.permissions.some((p) => p === permission);
  }

  canModifyClinicalData(isNoteBilled: boolean) {
    if (isNoteBilled) {
      return this.can('manage_notes');
    } else {
      return true;
    }
  }

  isAdminViewMode(): boolean {
    return this.locationContext.isAdminViewMode();
  }

  // ========== PASSWORD RESET METHODS ==========

  /**
   * Request password reset - Step 1
   * Returns available verification methods for the user
   */
  requestPasswordReset(email: string) {
    return this.http.post(AUTH_URLS.passwordResetRequest, { email }).pipe(
      catchError((error: HttpErrorResponse) => {
        this.errorHandler.handleError(error);
        throw error;
      }),
    );
  }

  /**
   * Send verification code for password reset - Step 2
   * Uses the existing 2FA infrastructure with password_reset context
   */
  sendPasswordResetCode(email: string, method: TwoFactorMethod) {
    return this.http
      .post<TwoFactorSendCodeResponse>(AUTH_URLS.twoFactorSendCode, {
        email,
        method,
        context: 'password_reset',
      })
      .pipe(
        tap((response) => {
          console.log('AuthService: Password reset code sent:', response);
        }),
        catchError((error: HttpErrorResponse) => {
          this.errorHandler.handleError(error);
          throw error;
        }),
      );
  }

  /**
   * Verify password reset code - Step 3
   * Returns a temporary reset token
   */
  verifyPasswordResetCode(email: string, code: string) {
    return this.http
      .post(AUTH_URLS.passwordResetVerify, {
        email,
        code,
      })
      .pipe(
        catchError((error: HttpErrorResponse) => {
          this.errorHandler.handleError(error);
          throw error;
        }),
      );
  }

  /**
   * Change password with reset token - Step 4
   * Final step to update the password
   */
  changePasswordWithReset(
    email: string,
    resetToken: string,
    password: string,
    passwordConfirmation: string,
  ) {
    return this.http
      .post(AUTH_URLS.passwordResetChange, {
        email,
        reset_token: resetToken,
        password,
        password_confirmation: passwordConfirmation,
      })
      .pipe(
        catchError((error: HttpErrorResponse) => {
          this.errorHandler.handleError(error);
          throw error;
        }),
      );
  }

  /**
   * Select location after authentication (2FA or direct login)
   */
  selectLocation(locationId: number) {
    const request: LocationSelectionRequest = { location_id: locationId };

    // Get temporary JWT token from storage
    const tempToken = this.storage.get<string>(AUTH_CONSTS.tempAuthToken);

    if (!tempToken) {
      console.error('Temporary auth token not found');
      throw new Error('Authentication token not found. Please login again.');
    }

    // Send request with JWT token in Authorization header
    const headers = {
      Authorization: `Bearer ${tempToken}`,
    };

    return this.http
      .post<LocationSelectionResponse>(AUTH_URLS.selectLocation, request, {
        headers,
      })
      .pipe(
        map((response) => {
          // Clean up temporary token
          this.clearPendingLoginState();

          // Store auth data
          const authResponse: Auth = {
            access_token: response.access_token,
            refresh_token: response.refresh_token,
            user: response.user,
          };

          const success = this.setUserToStorage(authResponse);

          if (success) {
            const isAdminView =
              locationId === AuthService.ADMIN_VIEW_LOCATION_ID ||
              !!response.user?.is_admin_view;
            this.locationContext.setAdminViewMode(isAdminView);
            if (isAdminView) {
              this.locationContext.clearCurrentLocationId();
            } else {
              this.locationContext.setCurrentLocationId(locationId);
            }
            this.getUserFromStorage();
          }

          return { success };
        }),
        catchError((error: HttpErrorResponse) => {
          // Clean up temporary token even on error
          this.clearPendingLoginState();
          this.errorHandler.handleError(error);
          throw error;
        }),
      );
  }

  /**
   * Change current location for authenticated user
   */
  changeLocation(locationId: number) {
    const request: ChangeLocationRequest = { location_id: locationId };

    return this.http
      .post<ChangeLocationResponse>(AUTH_URLS.changeLocation, request)
      .pipe(
        map((response) => {
          const responseUser = response.user;
          const baseUser = responseUser ?? this.user;
          const isAdminView =
            responseUser?.is_admin_view ??
            response.location?.is_admin_view ??
            locationId === AuthService.ADMIN_VIEW_LOCATION_ID;
          const nextLocationId = isAdminView
            ? locationId
            : responseUser?.location_id ?? response.location?.id ?? locationId;

          this.locationContext.setAdminViewMode(isAdminView);
          if (isAdminView) {
            this.locationContext.clearCurrentLocationId();
          } else {
            this.locationContext.setCurrentLocationId(nextLocationId);
          }

          // Update user context in memory with location-specific roles and permissions.
          if (baseUser) {
            const updatedUser: AuthUser = {
              ...baseUser,
              ...(responseUser ?? {}),
              location_id: nextLocationId,
              is_admin_view: isAdminView,
              roles: responseUser?.roles ?? response.roles ?? baseUser.roles ?? [],
              permissions:
                responseUser?.permissions ?? response.permissions ?? baseUser.permissions ?? [],
            };
            this.setUserAndEmit(updatedUser);
            this.syncImpersonationStateForLocationChange(
              updatedUser,
              isAdminView,
            );
          }

          return response;
        }),
        catchError((error: HttpErrorResponse) => {
          this.errorHandler.handleError(error);
          throw error;
        }),
      );
  }

  private syncImpersonationStateForLocationChange(
    updatedUser: AuthUser,
    isAdminView: boolean,
  ): void {
    const impersonationStateJson = sessionStorage.getItem(
      IMPERSONATION_CONSTS.state,
    );
    if (!impersonationStateJson) {
      return;
    }

    try {
      const impersonationState = JSON.parse(impersonationStateJson);
      const impersonatedUser = impersonationState?.impersonatedUser;

      if (!impersonationState?.isActive || !impersonatedUser?.id) {
        return;
      }

      if (impersonatedUser.id !== updatedUser.id) {
        return;
      }

      impersonationState.impersonatedUser = {
        ...impersonatedUser,
        roles: updatedUser.roles ?? impersonatedUser.roles ?? [],
        permissions:
          updatedUser.permissions ?? impersonatedUser.permissions ?? [],
        location_id: updatedUser.location_id,
        is_admin_view: isAdminView,
      };

      sessionStorage.setItem(
        IMPERSONATION_CONSTS.state,
        JSON.stringify(impersonationState),
      );
    } catch {
      // Ignore malformed impersonation state and continue location change flow.
    }
  }

  /**
   * Get all locations available to the authenticated user
   */
  getUserLocations() {
    const token = this.storage.get<string>(AUTH_CONSTS.token) || this.token;
    const headers = token ? { Authorization: `Bearer ${token}` } : undefined;

    return this.http
      .get<UserLocationsResponse>(AUTH_URLS.userLocations, { headers })
      .pipe(
        catchError((error: HttpErrorResponse) => {
          this.errorHandler.handleError(error);
          throw error;
        }),
      );
  }

  private appendAdminViewLocationIfAuthorized(
    locations: LocationForSelection[],
    permissions: string[],
  ): LocationForSelection[] {
    if (!Array.isArray(locations)) {
      return [];
    }

    const hasAdminViewPermission = permissions.includes('access_admin_view');
    const hasAdminViewOption = locations.some(
      (location) => location?.id === AuthService.ADMIN_VIEW_LOCATION_ID,
    );

    if (!hasAdminViewPermission || hasAdminViewOption) {
      return locations;
    }

    return [
      {
        id: AuthService.ADMIN_VIEW_LOCATION_ID,
        title: 'Admin View',
        avatar: null,
        city: null,
        state: null,
        is_admin_view: true,
      },
      ...locations,
    ];
  }
}
